import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { FormModal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { TextAreaField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';

import { INSTALLATION_REJECTED_MESSAGE } from '../constants';
import { useRejectInstallation } from '../hooks';
import { rejectInstallationSchema } from '../schemas';
import type { RejectInstallationValues } from '../types';

import styles from './reject-installation-modal.module.scss';

export type RejectInstallationModalProps = {
  open: boolean;
  id: string;
  onClose: () => void;
  onRejected: () => void;
  /** See `ApproveInstallationModalProps.onRefused` — same contract, same reason. */
  onRefused?: () => void;
};

export function RejectInstallationModal({
  open,
  id,
  onClose,
  onRejected,
  onRefused,
}: RejectInstallationModalProps) {
  const form = useForm<RejectInstallationValues>({
    resolver: zodResolver(rejectInstallationSchema),
    defaultValues: { reason: '' },
    mode: 'onChange',
  });
  const reject = useRejectInstallation(id);
  const toast = useToast();

  const submit = (values: RejectInstallationValues) =>
    // Trimmed here rather than in the schema: this is the one place the reason becomes a request
    // body, and a zod `.transform` would leak through `useForm`'s generic for no behavioural gain.
    reject.mutate(
      { reason: values.reason.trim() },
      {
        onSuccess: () => {
          toast.success({ title: INSTALLATION_REJECTED_MESSAGE });
          form.reset({ reason: '' });
          onRejected();
          onClose();
        },
        onError: (e) => {
          toast.error({
            title:
              e instanceof ApiError ? e.message : 'Could not reject the request. Please try again.',
          });
          onRefused?.();
        },
      },
    );

  return (
    <FormModal
      form={form}
      open={open}
      onClose={onClose}
      size="lg"
      centered={false}
      title={<h3 className={styles.title}>Reject Request</h3>}
      submitText="Reject"
      onSubmit={submit}
      loading={reject.isPending}
      primaryDisabled={!form.formState.isValid}
    >
      <div className={styles.grid}>
        <TextAreaField
          name="reason"
          control={form.control}
          label="Reason"
          placeholder="Enter a reason"
          rows={5}
          maxLength={2000}
          showCount
          required
        />
      </div>
    </FormModal>
  );
}
